import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import CheckCircle from '@material-ui/icons/CheckCircle';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import get from 'lodash/get';
import Error from '@material-ui/icons/Error';
import MainButton from '../../../MainButton';
import CustomDialog from '../../../CustomDialog';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetConfigurationCode,
  resetError,
  saveConfiguration,
} from '../../../../Reducers/Configurator/Actions';
import { setVisible, setWorkInProgress } from '../../../../Reducers/Ui/Actions';
import FileCopy from '@material-ui/icons/FileCopy';
import { Tooltip } from '@material-ui/core';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { useScreenshot } from '../../../ScreenshotContext';

const useStyles = makeStyles((theme) => ({
  dialogTitleRoot: {
    padding: '12px 12px 20px',
    fontSize: '1.5rem',
    fontWeight: 400,
  },
  dialogContentRoot: {
    padding: '0 24px 0 12px',
  },
  activeButton: {
    backgroundColor: '#83BD02',
    padding: '6px 15px',
    '&:hover': {
      backgroundColor: '#83BD02',
      opacity: 0.5,
    },
  },
  saveConfigurationCode: {
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 48,
    height: 40,
    fontSize: 24,
    lineHeight: '28px',
    [theme.breakpoints.down('xs')]: {
      height: 56,
      marginBottom: 32,
    },
  },
  TypographyCaption: {
    fontSize: 12,
    lineHeight: 1.7,
  },
  successResult: {
    background: '#83BD02',
    color: '#fff',
    padding: '14px 8px',
    borderRadius: '4px',
  },
  successResultIcon: {
    float: 'left',
  },
  errorResult: {
    background: '#ED1C29',
    color: '#fff',
    padding: '14px 8px',
    borderRadius: '4px',
    fontSize: 14,
    lineHeight: 1.7,
    textAlign: 'center',
    '& p': { color: '#fff !important' },
  },
  errorResultIcon: {
    float: 'left',
  },
  titleFieldWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24,
    marginBottom: 32,
  },
  titleField: {
    fontSize: '26px',
    borderRadius: 4,
    height: 40,
    fontWeight: 'bold',
    [theme.breakpoints.down('xs')]: {
      height: 56,
      width: '100%',
    },
  },
  titleFieldInput: {
    fontSize: '15px',
    lineHeight: '18px',
    textAlign: 'center',
    height: 20,
    padding: '10px 16px',
    minWidth: 320,
    [theme.breakpoints.down('xs')]: {
      padding: '0 5px',
      height: 56,
      minWidth: 50,
    },
  },
  dialogSavingInProgress: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'column',
    display: 'flex',
    height: 50,
    marginTop: 24,
  },
  copyShareUrlIcon: {
    float: 'right',
    '&:hover': {
      color: 'rgba(0,0,0, 0.5)',
      cursor: 'pointer',
    },
  },
}));

const SaveConfigurationDialog = (props) => {
  const { backgroundWorkMap, visibilityMap } = useSelector((state) => state.ui);
  const { configuration, error } = useSelector((state) => state.configurator);
  const [title, setTitle] = useState('');

  const show = !!visibilityMap.saveConfiguration;
  const workInProgress = !!backgroundWorkMap.saveConfiguration;
  const code = get(configuration, 'code', '');

  const classes = useStyles(props);
  const dispatch = useDispatch();
  const { takeScreenshot } = useScreenshot();

  useEffect(() => {
    show && dispatch(resetConfigurationCode());
  }, [show, dispatch]);

  const close = useCallback(async () => {
    await dispatch(setVisible('saveConfiguration', false));
    dispatch(resetError());
  }, [dispatch]);

  const confirm = async () => {
    await dispatch(setVisible('saveConfiguration', false));
  };

  const onChange = (e) => {
    if (error) {
      dispatch(resetError());
    }
    setTitle(e.target.value);
  };

  const onSaveClick = async () => {
    dispatch(resetError());
    dispatch(setWorkInProgress('saveConfiguration', true));
    const screenshot = takeScreenshot() || {};
    dispatch(resetConfigurationCode());
    dispatch(saveConfiguration(configuration, screenshot, 'user', { title }));
  };

  const copyToClipboard = (valueToCopy) => {
    try {
      navigator.clipboard.writeText(valueToCopy);
    } catch (err) {
      // clipboard api only available in secure contexts (localhost, https) and modern(ish) browsers
      const textArea = document.createElement('textarea');
      textArea.textContent = valueToCopy;
      textArea.style.position = 'fixed'; //avoid scrolling to bottom
      document.body.appendChild(textArea);
      const selection = document.getSelection();
      const range = document.createRange();
      range.selectNode(textArea);
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        document.execCommand('copy');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Configuration link could not be copied: ', err);
      } finally {
        selection.removeAllRanges();
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <CustomDialog
      open={show}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onCancel={close}
      onClose={close}
      dialogtitle={t('saveConfigurationDialog.dialogTitle')}
    >
      <DialogContent className={classes.dialogContentRoot}>
        {workInProgress && (
          <div className={classes.dialogSavingInProgress}>
            <CircularProgress />
          </div>
        )}
        {!workInProgress && !code && (
          <React.Fragment>
            <Typography
              variant="body1"
              classes={{ root: classes.TypographyCaption }}
              id="alert-dialog-description"
            >
              {t('saveConfigurationDialog.configurationTitleDescription')}
            </Typography>
            <div className={classes.titleFieldWrapper}>
              <TextField
                className={classes.titleField}
                value={title}
                variant={'outlined'}
                onChange={onChange}
                placeholder={t('saveConfigurationDialog.configurationTitle')}
                InputProps={{
                  classes: {
                    input: classes.titleFieldInput,
                  },
                }}
              />
            </div>
          </React.Fragment>
        )}
        {!workInProgress && code && (
          <React.Fragment>
            <Typography
              variant="body1"
              classes={{ root: classes.TypographyCaption }}
              id="alert-dialog-description"
            >
              {t('saveConfigurationDialog.saveSuccessful')}
            </Typography>

            <Typography
              variant="body1"
              classes={{ root: classes.TypographyCaption }}
              id="alert-dialog-description"
            >
              {t('saveConfigurationDialog.loadWithCode')}
            </Typography>
            <Typography
              id="alert-dialog-description"
              variant="h5"
              classes={{ root: classes.saveConfigurationCode }}
            >
              <div className={classes.successResult}>
                <CheckCircle className={classes.successResultIcon} />
                <b>{code}</b>
                <Tooltip title={t('saveConfigurationDialog.copyConfigurationCode')}>
                  <FileCopy className={classes.copyShareUrlIcon} onClick={() => copyToClipboard(code)} />
                </Tooltip>
              </div>
            </Typography>
          </React.Fragment>
        )}
        {error && (
          <div className={classes.errorResult}>
            <Error className={classes.errorResultIcon} />
            <Typography variant="body1">{t('saveConfigurationDialog.saveError')}</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {!workInProgress && code && (
          <MainButton onClick={confirm} variant="contained" size="medium" className={classes.activeButton}>
            {t('saveConfigurationDialog.ok')}
          </MainButton>
        )}
        {!workInProgress && !code && (
          <MainButton
            onClick={onSaveClick}
            variant="contained"
            size="medium"
            className={classes.activeButton}
          >
            {t('saveConfigurationDialog.confirm')}
          </MainButton>
        )}
      </DialogActions>
    </CustomDialog>
  );
};
export default SaveConfigurationDialog;
